import React from 'react';
import {
  HStack, SimpleTable, DetailStack,
  ActionIcon,
  ICON_PLUS,
  ICON_CLOSE,
  ICON_COMPARE_OUTLINE,
  useModal,
  Portal,
  Action,
  FormHandlers,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { PropertyUnit } from '../../../providers/api/dtos';
import { energyClassLabels, propertyUnitIntendedUseLabels } from '../../../labels';
import { NO_VALUE_SYMBOL } from '../../utils';
import { SuggestedPropertyUnit } from '../../hooks/use-ai-suggested-cadastral-registry/useAiSuggestedCadastralRegistry';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import PropertyUnitForm, { PropertyUnitFormModel } from '../property-unit-form/PropertyUnitForm';
import { toPropertyUnit, toPropertyUnitFormModel } from '../property-unit-form/mappers';

const highlightPropertyUnitField = (suggestedPropertyUnit: SuggestedPropertyUnit, field: keyof PropertyUnit) => (
  suggestedPropertyUnit.status === 'partial_match' && suggestedPropertyUnit.suggestion[field] !== suggestedPropertyUnit.existing?.[field]
    ? 'critical.default.low'
    : undefined
);

export interface SuggestedPropertyUnitTableRowProps extends React.AriaAttributes {
  suggestedPropertyUnit: SuggestedPropertyUnit,
  padded?: boolean,
  onAdd?: (addedPropertyUnit: PropertyUnit) => Promise<void> | void,
  onCompare?: (suggestedPropertyUnit: PropertyUnit, existingPropertyUnit: PropertyUnit) => Promise<void> | void,
  onIgnore?: (ignoredPropertyUnit: PropertyUnit) => Promise<void> | void,
}

const SuggestedPropertyUnitTableRow: React.FC<SuggestedPropertyUnitTableRowProps> = ({
  suggestedPropertyUnit,
  padded = false,
  onAdd,
  onCompare,
  onIgnore,
  ...rest
}) => {
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>() as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);

  const handleAdd = React.useCallback(async (formValues: PropertyUnitFormModel) => {
    if (suggestedPropertyUnit.status !== 'new') return;

    setIsSaving(true);

    const addedPropertyUnit = toPropertyUnit(formValues);

    await onAdd?.(addedPropertyUnit);

    setIsSaving(false);
    modal.close();
  }, [suggestedPropertyUnit.status, onAdd, modal]);

  const handleCompare = React.useCallback(async (formValues: PropertyUnitFormModel) => {
    if (suggestedPropertyUnit.status !== 'partial_match') return;

    setIsSaving(true);

    const updatedPropertyUnit = toPropertyUnit(formValues);

    await onCompare?.(updatedPropertyUnit, suggestedPropertyUnit.existing);

    setIsSaving(false);
    modal.close();
  }, [onCompare, suggestedPropertyUnit, modal]);

  return (
    <>
      <SimpleTable.Row
        aria-label="Riga di una unità immobiliare suggerita"
        padded={padded}
        {...rest}
      >
        <SimpleTable.Cell>
          <DetailStack
            label="Sezione"
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'section')}
          >
            {suggestedPropertyUnit.suggestion.section || NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Foglio"
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'sheet')}
          >
            {suggestedPropertyUnit.suggestion.sheet || NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Part."
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'parcel')}
          >
            {suggestedPropertyUnit.suggestion.parcel || NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Sub."
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'subunit')}
          >
            {suggestedPropertyUnit.suggestion.subunit || NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Cat."
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'category')}
          >
            {suggestedPropertyUnit.suggestion.category || NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Dest. d'uso"
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'intendedUse')}
          >
            {suggestedPropertyUnit.suggestion.intendedUse ? propertyUnitIntendedUseLabels[suggestedPropertyUnit.suggestion.intendedUse] : NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Rendita"
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'value')}
          >
            {suggestedPropertyUnit.suggestion.value ? formatEuro(suggestedPropertyUnit.suggestion.value) : NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell>
          <DetailStack
            label="Classe energ."
            color={highlightPropertyUnitField(suggestedPropertyUnit, 'energyClass')}
          >
            {suggestedPropertyUnit.suggestion.energyClass ? energyClassLabels[suggestedPropertyUnit.suggestion.energyClass] : NO_VALUE_SYMBOL}
          </DetailStack>
        </SimpleTable.Cell>

        <SimpleTable.Cell align="right">
          <HStack gap={75}>
            {suggestedPropertyUnit.status === 'new' && (
              <ActionIcon
                icon={{ path: ICON_PLUS }}
                label="Aggiungi"
                aria-label="Aggiungi unità immobiliare suggerita"
                onClick={() => modal.open()}
                size="S"
              />
            )}
            {suggestedPropertyUnit.status === 'partial_match' && (
              <ActionIcon
                icon={{ path: ICON_COMPARE_OUTLINE }}
                label="Compara/Aggiorna"
                aria-label="Compara e aggiorna unità immobiliare suggerita"
                onClick={() => modal.open()}
                size="S"
              />
            )}
            <ActionIcon
              label="Ignora"
              aria-label="Ignora unità immobiliare suggerita"
              onClick={() => onIgnore?.(suggestedPropertyUnit.suggestion)}
              icon={{ path: ICON_CLOSE }}
              size="S"
            />
          </HStack>
        </SimpleTable.Cell>
      </SimpleTable.Row>

      <Portal>
        <SimpleModal
          {...modal}
          title={
            suggestedPropertyUnit.status === 'partial_match'
              ? 'Compara e aggiorna unità immobiliare'
              : 'Aggiungi unità immobiliare'
          }
          aria-label={
            suggestedPropertyUnit.status === 'partial_match'
              ? "Modale per comparare e aggiornare l'unità immobiliare"
              : "Modale per aggiungere un'unità immobiliare"
          }
          footer={(
            <Action
              label={
                suggestedPropertyUnit.status === 'partial_match'
                  ? 'Aggiorna'
                  : 'Aggiungi'
              }
              color="primary"
              emphasis="high"
              onClick={() => formRef.current?.handleSubmit()}
              loading={isSaving}
            />
          )}
        >
          <PropertyUnitForm
            onSubmit={suggestedPropertyUnit.status === 'partial_match' ? handleCompare : handleAdd}
            initialValues={suggestedPropertyUnit.suggestion ? toPropertyUnitFormModel(suggestedPropertyUnit.suggestion) : {}}
            oldValues={suggestedPropertyUnit.status === 'partial_match' ? toPropertyUnitFormModel(suggestedPropertyUnit.existing) : {}}
            innerRef={formRef}
            hideSubmit
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default SuggestedPropertyUnitTableRow;
